import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ShieldCheck, Truck, Warehouse, ListTodo, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePermissions } from '../../contexts/PermissionsContext';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 15_000;

const TYPE_META = {
    aot: { icon: ShieldCheck, color: 'text-[#2e7cd6]', bg: 'bg-blue-50' },
    monte_meuble: { icon: Truck, color: 'text-amber-600', bg: 'bg-amber-50' },
    garde_meuble: { icon: Warehouse, color: 'text-violet-600', bg: 'bg-violet-50' },
    tache_effectuer: { icon: ListTodo, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    suppression: { icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' },
};

// ---------------------------------------------------------------------------
// Son de notification via Web Audio API (oscillateurs ding-dong).
// Plus fiable que HTMLAudioElement : un AudioContext "reprise" lors d'un
// geste utilisateur reste utilisable indéfiniment, y compris pour des sons
// déclenchés par setInterval.
// ---------------------------------------------------------------------------
let _audioCtx = null;
let _audioUnlocked = false;

function _getCtx() {
    if (_audioCtx) return _audioCtx;
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        _audioCtx = new Ctx();
    } catch {
        _audioCtx = null;
    }
    return _audioCtx;
}

function _playTone(ctx, freq, startOffset, duration, gainPeak) {
    // Note "cloche" : sinus fondamental + harmonique douce, attaque rapide,
    // décroissance exponentielle longue.
    const t0 = ctx.currentTime + startOffset;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(gainPeak, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    gain.connect(ctx.destination);

    // Fondamental (sinus pur)
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = freq;
    osc1.connect(gain);
    osc1.start(t0);
    osc1.stop(t0 + duration + 0.05);

    // Harmonique octave (triangle, plus discret) — donne un timbre "cristal"
    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = freq * 2;
    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0, t0);
    gain2.gain.linearRampToValueAtTime(gainPeak * 0.18, t0 + 0.015);
    gain2.gain.exponentialRampToValueAtTime(0.0001, t0 + duration * 0.6);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(t0);
    osc2.stop(t0 + duration + 0.05);
}

function playDing() {
    const ctx = _getCtx();
    if (!ctx) return;
    try {
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
        // Chime 3 notes ascendantes : C5 → E5 → G5 (Do majeur)
        _playTone(ctx, 523.25, 0.00, 1.20, 0.30); // C5
        _playTone(ctx, 659.25, 0.14, 1.20, 0.30); // E5
        _playTone(ctx, 783.99, 0.28, 1.40, 0.32); // G5
    } catch { /* noop */ }
}

// Déverrouille l'AudioContext via un geste utilisateur. Une fois "running",
// on peut déclencher des sons même depuis setInterval.
function _unlockAudio() {
    if (_audioUnlocked) return;
    const ctx = _getCtx();
    if (!ctx) return;
    try {
        if (ctx.state === 'suspended') {
            ctx.resume().then(() => {
                _audioUnlocked = ctx.state === 'running';
            }).catch(() => {});
        } else if (ctx.state === 'running') {
            _audioUnlocked = true;
        }
    } catch { /* noop */ }
}

function jourLabel(j) {
    if (j === 0) return "Aujourd'hui";
    if (j === 1) return 'Demain';
    return `dans ${j} j`;
}

const SEEN_STORAGE_KEY = 'notif_seen_ids_v1';

function loadSeenIds() {
    try {
        const raw = localStorage.getItem(SEEN_STORAGE_KEY);
        if (!raw) return new Set();
        return new Set(JSON.parse(raw));
    } catch {
        return new Set();
    }
}

function persistSeenIds(set) {
    try {
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(Array.from(set)));
    } catch { /* noop */ }
}

export function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [seenIds, setSeenIds] = useState(() => loadSeenIds());
    const knownIdsRef = useRef(new Set()); // ids déjà vus dans le poll (pour détecter les NOUVEAUX arrivants)
    const firstLoadRef = useRef(true);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { hasFlag, can } = usePermissions();

    // Compteur de la pastille = uniquement les notifications NON vues
    const unseenCount = items.filter(x => !seenIds.has(x.id)).length;

    const markAsSeen = (id) => {
        setSeenIds(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            persistSeenIds(next);
            return next;
        });
    };

    const markAllAsSeen = () => {
        setSeenIds(prev => {
            const next = new Set(prev);
            items.forEach(it => next.add(it.id));
            persistSeenIds(next);
            return next;
        });
    };

    const fetchUrgences = async () => {
        try {
            const res = await axios.get(`${API}/api/notifications/urgences`, { withCredentials: true });
            const list = res.data?.urgences || [];
            setItems(list);

            const incomingIds = new Set(list.map(x => x.id));

            // Nettoyage : on retire les IDs "seen" pour des notifs qui n'existent plus
            setSeenIds(prev => {
                const next = new Set();
                prev.forEach(id => { if (incomingIds.has(id)) next.add(id); });
                if (next.size !== prev.size) persistSeenIds(next);
                return next;
            });

            if (firstLoadRef.current) {
                knownIdsRef.current = incomingIds;
                firstLoadRef.current = false;
            } else {
                const newOnes = list.filter(x => !knownIdsRef.current.has(x.id));
                if (newOnes.length > 0) {
                    playDing();
                    const firstNew = newOnes[0];
                    const more = newOnes.length > 1 ? ` (+${newOnes.length - 1})` : '';
                    toast.info(
                        `Nouvelle ${firstNew.type_label}${more}`,
                        { description: firstNew.client_nom || firstNew.message },
                    );
                }
                knownIdsRef.current = incomingIds;
            }
        } catch {
            // Silencieux
        }
    };

    useEffect(() => {
        fetchUrgences();
        const id = setInterval(fetchUrgences, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    // Unlock audio dès la première interaction utilisateur (click/keydown/touch).
    useEffect(() => {
        const unlock = () => {
            _unlockAudio();
            if (_audioUnlocked) {
                window.removeEventListener('click', unlock);
                window.removeEventListener('keydown', unlock);
                window.removeEventListener('touchstart', unlock);
            }
        };
        window.addEventListener('click', unlock);
        window.addEventListener('keydown', unlock);
        window.addEventListener('touchstart', unlock);
        return () => {
            window.removeEventListener('click', unlock);
            window.removeEventListener('keydown', unlock);
            window.removeEventListener('touchstart', unlock);
        };
    }, []);

    // Fermer la dropdown au clic extérieur
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const goTo = (route, id) => {
        markAsSeen(id);
        setOpen(false);
        navigate(route);
    };

    const newItems = items.filter(x => !seenIds.has(x.id));
    const oldItems = items.filter(x => seenIds.has(x.id));

    const renderItem = (it, isSeen) => {
        const meta = TYPE_META[it.type] || TYPE_META.aot;
        const Icon = meta.icon;
        const j = it.jours_restants ?? null;
        const isTask = it.type === 'tache_effectuer';
        const danger = j !== null && j <= 2;
        return (
            <li key={it.id}>
                <button
                    onClick={() => goTo(it.route, it.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 items-start ${isSeen ? 'opacity-60' : ''}`}
                    data-testid={`notification-item-${it.id}`}
                >
                    <div className={`w-9 h-9 rounded-lg ${meta.bg} flex items-center justify-center shrink-0 relative`}>
                        <Icon size={16} className={meta.color} />
                        {!isSeen && (
                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${isSeen ? 'font-medium text-slate-600' : 'font-semibold text-slate-800'}`}>
                                {it.client_nom || (isTask ? 'Tâche' : 'Client')}
                            </p>
                            {isTask ? (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${isSeen ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`}>
                                    Nouvelle
                                </span>
                            ) : j !== null ? (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${danger ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                                    {jourLabel(j)}
                                </span>
                            ) : null}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{it.type_label} · {it.message}</p>
                    </div>
                </button>
            </li>
        );
    };

    return (
        // Cache la cloche si l'utilisateur n'a pas le droit de recevoir les notifications
        // OU si le module 'notifications' est en niveau 'none'.
        (!hasFlag('receive_notifications') || !can('notifications', 'read')) ? null :
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setOpen(o => !o)}
                className="relative w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                title="Urgences"
                data-testid="notification-bell"
            >
                <Bell
                    size={20}
                    className={unseenCount > 0 ? 'text-[#2e7cd6] animate-[shake_1s_ease-in-out]' : 'text-slate-500'}
                    strokeWidth={2}
                />
                {unseenCount > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
                        data-testid="notification-bell-badge"
                    >
                        {unseenCount > 99 ? '99+' : unseenCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-12 w-[360px] max-h-[70vh] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 flex flex-col overflow-hidden"
                    data-testid="notification-bell-dropdown"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Notifications
                            </h3>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                {unseenCount === 0
                                    ? `${items.length === 0 ? 'Aucune notification' : 'Tout est lu'}`
                                    : `${unseenCount} nouvelle${unseenCount > 1 ? 's' : ''}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-1">
                            {unseenCount > 0 && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); markAllAsSeen(); }}
                                    className="text-[10px] px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-[#2e7cd6] font-medium"
                                    title="Tout marquer comme lu"
                                    data-testid="notification-bell-mark-all-read"
                                >
                                    Tout lire
                                </button>
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); _unlockAudio(); playDing(); }}
                                className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium"
                                title="Tester le son de notification"
                                data-testid="notification-bell-test-sound"
                            >
                                Tester le son
                            </button>
                            <button
                                onClick={() => setOpen(false)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                                data-testid="notification-bell-close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center px-6">
                                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                                    <Bell size={20} className="text-emerald-600" />
                                </div>
                                <p className="text-sm text-slate-600 font-medium">Tout est sous contrôle</p>
                                <p className="text-xs text-slate-400 mt-1">Aucune notification en cours.</p>
                            </div>
                        ) : (
                            <>
                                {newItems.length > 0 && (
                                    <div data-testid="notification-section-new">
                                        <div className="px-4 py-1.5 bg-blue-50/40 border-b border-blue-100">
                                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#2e7cd6]">
                                                Nouvelles ({newItems.length})
                                            </p>
                                        </div>
                                        <ul className="divide-y divide-slate-100">
                                            {newItems.map(it => renderItem(it, false))}
                                        </ul>
                                    </div>
                                )}
                                {oldItems.length > 0 && (
                                    <div data-testid="notification-section-seen">
                                        <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-100">
                                            <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">
                                                Déjà vues ({oldItems.length})
                                            </p>
                                        </div>
                                        <ul className="divide-y divide-slate-100">
                                            {oldItems.map(it => renderItem(it, true))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes shake {
                    0%, 100% { transform: rotate(0deg); }
                    20% { transform: rotate(-12deg); }
                    40% { transform: rotate(10deg); }
                    60% { transform: rotate(-6deg); }
                    80% { transform: rotate(4deg); }
                }
            `}</style>
        </div>
    );
}
